/**
 * ErrorBoundary - Generic React Error Boundary
 *
 * Catches JavaScript errors in child components and displays a fallback UI.
 * Prevents the entire app from crashing due to component errors.
 */

import { Component, ErrorInfo, ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";

// ============================================================================
// Types
// ============================================================================

interface ErrorBoundaryProps {
  /** Child components to protect */
  children: ReactNode;
  /** Custom fallback component */
  fallback?: ReactNode;
  /** Callback when error is caught */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** Title for the error display */
  title?: string;
  /** Whether to show retry button */
  showRetry?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

// ============================================================================
// Default Fallback Component
// ============================================================================

interface DefaultFallbackProps {
  error: Error | null;
  title: string;
  showRetry: boolean;
  onRetry: () => void;
}

function DefaultFallback({ error, title, showRetry, onRetry }: DefaultFallbackProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-4 p-6",
        "rounded-lg border border-red-200 bg-red-50"
      )}
      role="alert"
    >
      <AlertTriangle className="h-10 w-10 text-red-500" aria-hidden="true" />
      <div className="text-center">
        <h3 className="text-sm font-semibold text-red-800">{title}</h3>
        {error && (
          <p className="mt-1 text-xs text-red-600">
            {error.message || "An unexpected error occurred"}
          </p>
        )}
      </div>
      {showRetry && (
        <button
          onClick={onRetry}
          className={cn(
            "flex items-center gap-2 rounded-lg px-4 py-2",
            "bg-red-600 text-white text-sm font-medium",
            "hover:bg-red-700 transition-colors",
            "focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
          )}
        >
          <RefreshCw className="h-4 w-4" />
          Try Again
        </button>
      )}
    </div>
  );
}

// ============================================================================
// Error Boundary Component
// ============================================================================

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error to console in development
    console.error("ErrorBoundary caught an error:", error, errorInfo);

    // Call optional error callback
    this.props.onError?.(error, errorInfo);
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    const { hasError, error } = this.state;
    const {
      children,
      fallback,
      title = "Something went wrong",
      showRetry = true,
    } = this.props;

    if (hasError) {
      // Use custom fallback if provided
      if (fallback) {
        return fallback;
      }

      // Use default fallback
      return (
        <DefaultFallback
          error={error}
          title={title}
          showRetry={showRetry}
          onRetry={this.handleRetry}
        />
      );
    }

    return children;
  }
}

// ============================================================================
// Specialized AI Error Boundary
// ============================================================================

interface AIErrorBoundaryProps {
  children: ReactNode;
  componentName?: string;
}

export function AIErrorBoundary({
  children,
  componentName = "AI Assistant",
}: AIErrorBoundaryProps) {
  return (
    <ErrorBoundary
      title={`${componentName} encountered an error`}
      showRetry
      onError={(error) => {
        // In production, you would send this to an error tracking service
        console.error(`[${componentName}] Error:`, error);
      }}
    >
      {children}
    </ErrorBoundary>
  );
}

export default ErrorBoundary;
